import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaGithub, FaExternalLinkAlt, FaArrowLeft, FaCheckCircle,
  FaRobot, FaSearch, FaLink, FaFileAlt, FaChartBar, FaLightbulb,
  FaShieldAlt, FaTerminal, FaWrench
} from 'react-icons/fa';
import { aiProjectsData } from '../data/aiProjects';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './AIProjectDetail.css';

const toolIconMap = {
  search: <FaSearch />,
  link: <FaLink />,
  file: <FaFileAlt />,
  report: <FaChartBar />,
  github: <FaGithub />,
  shield: <FaShieldAlt />,
  terminal: <FaTerminal />,
  wrench: <FaWrench />,
};

const TABS = ['Overview', 'How It Works', 'Stack', 'Outcomes'];

const AIProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Overview');
  const project = aiProjectsData.projects.find((p) => p.id === id);

  useEffect(() => { window.scrollTo(0, 0); setActiveTab('Overview'); }, [id]);

  if (!project) {
    return (
      <div className="dpx-not-found">
        <Navbar />
        <div className="dpx-not-found-content">
          <h2>Project not found</h2>
          <button className="dpx-back-btn" onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Portfolio
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="dpx-page">
      <Navbar />

      {/* ── Hero ── */}
      <div className="dpx-hero">
        <div className="dpx-hero-inner">
          <div className="dpx-hero-content">
            <button className="dpx-back-btn" onClick={() => navigate('/')}>
              <FaArrowLeft /> Back to Projects
            </button>
            <div className="dpx-badge">
              <FaRobot /> AI / ML Project
            </div>
            <h1 className="dpx-title">{project.title}</h1>
            <p className="dpx-tagline">{project.tagline}</p>
            <div className="dpx-tags">
              {project.technologies.map((t, i) => (
                <span key={i} className="dpx-tag">{t}</span>
              ))}
            </div>
            <div className="dpx-hero-actions">
              {project.github && (
                <a href={project.github} target="_blank" rel="noopener noreferrer" className="dpx-btn dpx-btn--primary">
                  <FaGithub /> GitHub
                </a>
              )}
              {project.demo && (
                <a href={project.demo} target="_blank" rel="noopener noreferrer" className="dpx-btn dpx-btn--secondary">
                  <FaExternalLinkAlt /> Live Demo
                </a>
              )}
            </div>
          </div>

          {(project.coverImage || project.image) && (
            <div className="dpx-hero-img-wrap">
              <img src={project.coverImage || project.image} alt={project.title} className="dpx-hero-img" />
            </div>
          )}
        </div>

        {/* Stats bar */}
        {project.stats && (
          <div className="dpx-stats-bar">
            <div className="dpx-stats-inner">
              {project.stats.map((s, i) => (
                <div key={i} className="dpx-stat">
                  <span className="dpx-stat-value">{s.value}</span>
                  <span className="dpx-stat-label">{s.label}</span>
                  <span className="dpx-stat-sub">{s.sublabel}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Tab bar ── */}
      <div className="dpx-tab-bar">
        <div className="dpx-tab-bar-inner">
          {TABS.map(tab => (
            <button
              key={tab}
              className={`dpx-tab-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => { setActiveTab(tab); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div className="dpx-body">

        {/* ── Overview ── */}
        {activeTab === 'Overview' && (
          <div className="dpx-panel">
            <p className="dpx-lead">{project.overview}</p>
            <div className="dpx-two-col">
              <div className="dpx-card dpx-card--red">
                <div className="dpx-card-label dpx-card-label--red">The Problem</div>
                <p className="dpx-prose">{project.problemStatement}</p>
              </div>
              <div className="dpx-card dpx-card--green">
                <div className="dpx-card-label dpx-card-label--green">The Solution</div>
                <p className="dpx-prose">{project.solution}</p>
              </div>
            </div>
            {project.keyFeatures && (
              <>
                <div className="dpx-section-label">Key Features</div>
                <div className="dpx-features-grid">
                  {project.keyFeatures.map((f, i) => (
                    <div key={i} className="dpx-feature">
                      <FaCheckCircle className="dpx-feature-icon" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── How It Works ── */}
        {activeTab === 'How It Works' && (
          <div className="dpx-panel dpx-panel--split">
            {project.howItWorks && (
              <div className="dpx-steps-col">
                <div className="dpx-section-label">Workflow</div>
                <div className="dpx-steps">
                  {project.howItWorks.map((item, i) => (
                    <div key={i} className="dpx-step">
                      <div className="dpx-step-marker">
                        <div className="dpx-step-num">{i + 1}</div>
                        {i < project.howItWorks.length - 1 && <div className="dpx-step-line" />}
                      </div>
                      <div className="dpx-step-body">
                        <p className="dpx-step-title">{item.step.replace(/^\d+\.\s*/, '')}</p>
                        <p className="dpx-step-detail">{item.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {project.agentTools && (
              <div className="dpx-tools-col">
                <div className="dpx-section-label">Agent Tools</div>
                <div className="dpx-tools-grid">
                  {project.agentTools.map((tool, i) => (
                    <div key={i} className="dpx-tool-card">
                      <div className="dpx-tool-icon">
                        {toolIconMap[tool.icon] || <FaRobot />}
                      </div>
                      <div className="dpx-tool-info">
                        <p className="dpx-tool-name">{tool.name}</p>
                        <p className="dpx-tool-desc">{tool.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Stack ── */}
        {activeTab === 'Stack' && (
          <div className="dpx-panel">
            {project.techStack && (
              <>
                <div className="dpx-section-label">Tech Stack by Layer</div>
                <div className="dpx-stack-table">
                  {project.techStack.map((row, i) => (
                    <div key={i} className="dpx-stack-row">
                      <div className="dpx-stack-layer">{row.layer}</div>
                      <div className="dpx-stack-what">{row.what}</div>
                      <div className="dpx-stack-tech">{row.tech}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {project.architectureDiagram ? (
              <>
                <div className="dpx-section-label dpx-mt">Architecture Diagram</div>
                <div className="dpx-arch-diagram">
                  <img src={project.architectureDiagram} alt="Architecture" className="dpx-arch-img" />
                </div>
              </>
            ) : project.architecture ? (
              <>
                <div className="dpx-section-label dpx-mt">Architecture</div>
                <ul className="dpx-arch-list">
                  {project.architecture.map((item, i) => (
                    <li key={i} className="dpx-arch-item">
                      <span className="dpx-arch-num">{String(i + 1).padStart(2, '0')}</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        )}

        {/* ── Outcomes ── */}
        {activeTab === 'Outcomes' && (
          <div className="dpx-panel">
            {project.resultStats && (
              <>
                <div className="dpx-section-label">By the Numbers</div>
                <div className="dpx-result-stats">
                  {project.resultStats.map((r, i) => (
                    <div key={i} className="dpx-result-card">
                      <span className="dpx-result-val">{r.value}</span>
                      <span className="dpx-result-label">{r.label}</span>
                      <span className="dpx-result-ctx">{r.context}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
            {project.results && (
              <p className="dpx-lead">{project.results}</p>
            )}
            {project.challenges && (
              <>
                <div className="dpx-section-label dpx-mt">Challenges & How I Solved Them</div>
                <div className="dpx-challenges">
                  {(Array.isArray(project.challenges) ? project.challenges : [{ body: project.challenges }]).map((c, i) => (
                    <div key={i} className="dpx-challenge">
                      <div className="dpx-challenge-icon"><FaLightbulb /></div>
                      <div className="dpx-challenge-body">
                        {c.title && <p className="dpx-challenge-title">{c.title}</p>}
                        <p className="dpx-challenge-text">{c.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {project.lessonsLearned && (
              <div className="dpx-lessons-card">
                <div className="dpx-section-label">Lessons Learned</div>
                <p className="dpx-prose">{project.lessonsLearned}</p>
              </div>
            )}
          </div>
        )}

      </div>

      {/* ── CTA bar ── */}
      <div className="dpx-cta-bar">
        <div className="dpx-cta-inner">
          {project.github && (
            <a href={project.github} target="_blank" rel="noopener noreferrer" className="dpx-btn dpx-btn--primary">
              <FaGithub /> View Source Code
            </a>
          )}
          {project.demo && (
            <a href={project.demo} target="_blank" rel="noopener noreferrer" className="dpx-btn dpx-btn--secondary">
              <FaExternalLinkAlt /> Live Demo
            </a>
          )}
          <button className="dpx-btn dpx-btn--outline" onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to AI Projects
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AIProjectDetail;
